import React, { useEffect, useRef, useState } from "react";
import { Music, Pause, SkipForward } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { resolveMedia } from "@/lib/api";

export function MusicPlayer() {
  const { settings } = useSettings();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [index, setIndex] = useState(0);

  const tracks = (settings.music_tracks && settings.music_tracks.length
    ? settings.music_tracks
    : (settings.music_url ? [settings.music_url] : [])
  ).filter(Boolean);
  const enabled = settings.music_enabled && tracks.length > 0;
  const current = tracks[index] || tracks[0] || "";

  useEffect(() => { setIndex(0); }, [settings.music_tracks, settings.music_url]);

  useEffect(() => {
    if (!enabled) { setPlaying(false); return; }
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = 0.35;

    const tryPlay = () => audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    tryPlay();

    const onInteract = () => { if (audio.paused) tryPlay(); cleanup(); };
    const cleanup = () => {
      window.removeEventListener("pointerdown", onInteract);
      window.removeEventListener("keydown", onInteract);
    };
    window.addEventListener("pointerdown", onInteract);
    window.addEventListener("keydown", onInteract);
    return cleanup;
  }, [enabled, current]);

  if (!enabled) return null;

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play().then(() => setPlaying(true)).catch(() => {});
    else { audio.pause(); setPlaying(false); }
  };

  const next = () => {
    if (tracks.length < 2) return;
    setIndex((i) => (i + 1) % tracks.length);
  };

  const onEnded = () => {
    if (tracks.length < 2) {
      const audio = audioRef.current;
      if (audio) audio.play().catch(() => {});
      return;
    }
    setIndex((i) => (i + 1) % tracks.length);
  };

  return (
    <>
      <audio ref={audioRef} src={resolveMedia(current)} onEnded={onEnded} preload="auto" />
      <MusicControls playing={playing} onToggle={toggle} onNext={next} hasMultiple={tracks.length > 1} />
    </>
  );
}

function MusicControls({ playing, onToggle, onNext, hasMultiple }) {
  return (
    <div className="fixed bottom-5 left-5 z-50 flex items-center gap-2">
      <button
        onClick={onToggle}
        data-testid="music-toggle-btn"
        title={playing ? "Pause music" : "Play music"}
        aria-label={playing ? "Pause background music" : "Play background music"}
        className="group flex items-center gap-3 bg-black/70 backdrop-blur-md border border-[#FF5722]/60 hover:border-[#FF5722] text-white pl-3 pr-4 py-2.5 rounded-full shadow-lg shadow-black/40 transition-all duration-200"
      >
        <span className="relative flex items-center justify-center w-7 h-7 shrink-0">
          {playing && <span className="absolute inset-0 rounded-full bg-[#FF5722]/40 animate-ping" />}
          <span className="relative flex items-center justify-center w-7 h-7 rounded-full bg-[#FF5722] text-black">
            {playing ? <Pause size={14} fill="currentColor" /> : <Music size={14} />}
          </span>
        </span>
        {playing ? (
          <span className="flex items-end gap-[3px] h-4" aria-hidden="true">
            <span className="w-[3px] bg-[#FF5722] eq-bar" style={{ animationDelay: "0ms" }} />
            <span className="w-[3px] bg-[#FF5722] eq-bar" style={{ animationDelay: "150ms" }} />
            <span className="w-[3px] bg-[#FF5722] eq-bar" style={{ animationDelay: "300ms" }} />
            <span className="w-[3px] bg-[#FF5722] eq-bar" style={{ animationDelay: "450ms" }} />
          </span>
        ) : (
          <span className="text-xs font-bold uppercase tracking-wider text-white/80 group-hover:text-white">
            Play Music
          </span>
        )}
      </button>
      {hasMultiple && (
        <button
          onClick={onNext}
          data-testid="music-next-btn"
          title="Next track"
          aria-label="Next track"
          className="flex items-center justify-center w-10 h-10 bg-black/70 backdrop-blur-md border border-[#FF5722]/60 hover:border-[#FF5722] text-white rounded-full shadow-lg shadow-black/40 transition-all duration-200"
        >
          <SkipForward size={16} />
        </button>
      )}
    </div>
  );
}
